/**
 * Purchase Orders page.
 *
 * Tab filter for status, KPI strip, DataTable with row click → detail Sheet.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ClipboardList, Pencil, Plus } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  toast,
  cn,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import {
  PoStatusBadge,
  type PoStatus,
} from "../../../components/imports/PoStatusBadge";
import { LcStatusBadge } from "../../../components/imports/LcStatusBadge";
import {
  findSupplier,
  findLc,
  findWarehouse,
  mockBranches,
  mockLettersOfCredit,
  mockProducts,
  mockPurchaseOrders,
  mockShipments,
  mockSuppliers,
  mockUnits,
  mockWarehouses,
} from "../../../components/imports/importsMockData";

type MockBranch = (typeof mockBranches)[number];
type MockLetterOfCredit = (typeof mockLettersOfCredit)[number];
type MockProductRecord = (typeof mockProducts)[number];
type MockPurchaseOrderRecord = (typeof mockPurchaseOrders)[number];
type MockShipmentRecord = (typeof mockShipments)[number];
type MockSupplier = (typeof mockSuppliers)[number];
type MockUnitRecord = (typeof mockUnits)[number];
type MockWarehouseRecord = (typeof mockWarehouses)[number];
import { PoForm, type PoFormValues } from "../../../components/forms/PoForm";
import {
  SupplierForm,
  type SupplierFormValues,
} from "../../../components/forms/SupplierForm";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import { useHasPermission } from "../../../lib/permissions";
import { ShipmentStatusBadge } from "../../../components/imports/ShipmentStatusBadge";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";
import {
  AttachmentBadge,
  AttachmentList,
} from "../../../components/attachments";

const PO_PURPOSE_OPTIONS = [
  { value: "po_pdf", label: "PO PDF" },
  { value: "supplier_acknowledgement", label: "Supplier Ack" },
  { value: "quote", label: "Quote" },
  { value: "amendment", label: "Amendment" },
  { value: "spec_sheet", label: "Spec Sheet" },
];

export const Route = createFileRoute("/_app/imports/purchase-orders")({
  component: PurchaseOrdersPage,
});

const NOW = Date.now();
const DAY = 24 * 60 * 60 * 1000;

type PoRow = {
  _id: string;
  poNumber: string;
  supplierId: string;
  letterOfCreditId: string | null;
  branchId: string;
  destinationWarehouseId: string | null;
  status: PoStatus;
  currency: string;
  totalAmountCents: bigint;
  totalAmountInBaseCents: bigint;
  orderDate: number;
  expectedDeliveryDate: number | null;
  notes: string | null;
  updatedAt: number;
  /** Mock-only: number of lines on the PO. Derived for table display. */
  lineCount: number;
};

function toPoRow(raw: MockPurchaseOrderRecord): PoRow {
  return {
    _id: raw._id,
    poNumber: raw.poNumber,
    supplierId: raw.supplierId,
    letterOfCreditId: raw.letterOfCreditId,
    branchId: raw.branchId,
    destinationWarehouseId: raw.destinationWarehouseId,
    status: raw.status,
    currency: raw.currency,
    totalAmountCents: raw.totalAmountCents,
    totalAmountInBaseCents: raw.totalAmountInBaseCents,
    orderDate: raw.orderDate,
    expectedDeliveryDate: raw.expectedDeliveryDate,
    notes: raw.notes,
    updatedAt: raw.updatedAt,
    lineCount: 1, // mock; real mode joins purchaseOrderLines.
  };
}

function formatDate(ms: number | null): string {
  if (ms === null) return "—";
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function relativeTime(ms: number): string {
  const diff = NOW - ms;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ms).toLocaleDateString("en-BD");
}

const TAB_OPTIONS: {
  value: "all" | "draft" | "submitted" | "in_transit" | "received" | "closed";
  label: string;
}[] = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "in_transit", label: "In transit" },
  { value: "received", label: "Received" },
  { value: "closed", label: "Closed" },
];

function PurchaseOrdersPage() {
  const isMock = useMockData();
  // PO totals leak supplier-cost data — hide from non-finance roles. Status,
  // quantity, supplier and expected delivery stay visible (operational).
  const canSeeAmounts = useHasPermission("finance.read");

  const poSeed = useMemo(
    () => (mockPurchaseOrders as readonly MockPurchaseOrderRecord[]).map(toPoRow),
    [],
  );
  const [seedRows, setSeedRows] = usePersistentMockState<PoRow[]>("purchaseOrders", poSeed);
  const rows = seedRows;

  const [lettersOfCredit] = usePersistentMockState<MockLetterOfCredit[]>(
    "lettersOfCredit",
    mockLettersOfCredit,
  );
  const [branches] = usePersistentMockState<MockBranch[]>(
    "branches",
    mockBranches,
  );
  const [warehouses] = usePersistentMockState<MockWarehouseRecord[]>(
    "warehouses",
    mockWarehouses,
  );
  const [products] = usePersistentMockState<MockProductRecord[]>(
    "products",
    mockProducts,
  );
  const [units] = usePersistentMockState<MockUnitRecord[]>("units", mockUnits);
  const [shipments] = usePersistentMockState<MockShipmentRecord[]>(
    "shipments",
    mockShipments,
  );

  // Local mutable supplier list so opening-balance edits persist in mock mode.
  type LocalSupplier = MockSupplier & {
    openingBalanceBaseCents?: bigint;
    openingBalanceAsOf?: number | null;
  };
  const [localSuppliers, setLocalSuppliers] = useState<LocalSupplier[]>(
    () => mockSuppliers as LocalSupplier[],
  );
  const [editingSupplier, setEditingSupplier] =
    useState<LocalSupplier | null>(null);

  const [tab, setTab] =
    useState<(typeof TAB_OPTIONS)[number]["value"]>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<PoRow | null>(null);
  const [editingPo, setEditingPo] = useState<PoRow | null>(null);

  const filtered = useMemo(() => {
    if (tab === "all") return rows;
    if (tab === "draft") return rows.filter((r) => r.status === "draft");
    if (tab === "submitted")
      return rows.filter(
        (r) => r.status === "submitted" || r.status === "acknowledged",
      );
    if (tab === "in_transit")
      return rows.filter((r) => r.status === "partially_received");
    if (tab === "received") return rows.filter((r) => r.status === "received");
    if (tab === "closed")
      return rows.filter(
        (r) => r.status === "closed" || r.status === "cancelled",
      );
    return rows;
  }, [rows, tab]);

  // KPIs
  const openPOs = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.status !== "closed" &&
          r.status !== "received" &&
          r.status !== "cancelled",
      ),
    [rows],
  );
  const totalOpenBdt = useMemo(
    () => openPOs.reduce((s, r) => s + r.totalAmountInBaseCents, 0n),
    [openPOs],
  );
  const awaitingReceipt = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.status === "acknowledged" || r.status === "partially_received",
      ).length,
    [rows],
  );
  const thisMonth = useMemo(() => {
    const start = new Date();
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    return rows.filter((r) => r.orderDate >= start.getTime()).length;
  }, [rows]);

  const columns = useMemo<ColumnDef<PoRow>[]>(
    () => {
      const cols: ColumnDef<PoRow>[] = [
        {
          accessorKey: "poNumber",
          header: "PO Number",
          cell: ({ row }) => (
            <span className="font-mono text-[12px] text-(--text-primary)">
              {row.original.poNumber}
            </span>
          ),
          meta: { mono: true },
        },
        {
          id: "supplier",
          header: "Supplier",
          cell: ({ row }) => (
            <span className="text-(--text-primary)">
              {findSupplier(row.original.supplierId)?.displayName ?? "—"}
            </span>
          ),
        },
        {
          id: "lc",
          header: "LC",
          cell: ({ row }) => {
            const lc = findLc(row.original.letterOfCreditId);
            if (!lc) {
              return <span className="text-(--text-tertiary)">—</span>;
            }
            return (
              <span className="font-mono text-[11px] text-(--info)">
                {lc.lcNumber}
              </span>
            );
          },
          meta: { mono: true, hideOnMobile: true },
        },
        {
          accessorKey: "currency",
          header: "Curr.",
          cell: ({ row }) => (
            <Badge variant="neutral" className="font-mono">
              {row.original.currency}
            </Badge>
          ),
          meta: { hideOnMobile: true },
        },
      ];

      if (canSeeAmounts) {
        cols.push(
          {
            id: "amount",
            header: "Amount",
            cell: ({ row }) => (
              <span className="font-mono tabular-nums text-(--text-primary)">
                {formatMoney(row.original.totalAmountCents, row.original.currency, {
                  display: "code",
                  locale: "en-BD",
                })}
              </span>
            ),
            meta: { numeric: true, mono: true },
          },
          {
            id: "amountBdt",
            header: "BDT",
            cell: ({ row }) => (
              <span className="font-mono tabular-nums text-(--text-secondary)">
                {formatMoney(row.original.totalAmountInBaseCents, "BDT", {
                  locale: "en-BD",
                })}
              </span>
            ),
            meta: { numeric: true, mono: true, hideOnMobile: true },
          },
        );
      }

      cols.push(
        {
          accessorKey: "lineCount",
          header: "Lines",
          cell: ({ row }) => (
            <span className="tabular-nums text-(--text-secondary)">
              {row.original.lineCount}
            </span>
          ),
          meta: { numeric: true, hideOnMobile: true },
        },
        {
          accessorKey: "status",
          header: "Status",
          cell: ({ row }) => <PoStatusBadge status={row.original.status} />,
        },
        {
          accessorKey: "expectedDeliveryDate",
          header: "Expected",
          cell: ({ row }) => (
            <span className="text-(--text-secondary) tabular-nums">
              {formatDate(row.original.expectedDeliveryDate)}
            </span>
          ),
          meta: { hideOnMobile: true },
        },
        {
          accessorKey: "updatedAt",
          header: "Updated",
          cell: ({ row }) => (
            <span className="text-[12px] text-(--text-tertiary)">
              {relativeTime(row.original.updatedAt)}
            </span>
          ),
          meta: { hideOnMobile: true },
        },
      );

      return cols;
    },
    [canSeeAmounts],
  );

  function handleCreate(values: PoFormValues) {
    const id = `po_local_${Date.now()}`;
    const subtotalCents = values.lines.reduce(
      (s, l) =>
        s + BigInt(Math.trunc(Math.max(l.orderedQuantity, 0))) * l.unitPriceCents,
      0n,
    );
    const newRow: PoRow = {
      _id: id,
      poNumber: `PO-${new Date().getFullYear()}-${String(rows.length + 1).padStart(6, "0")}`,
      supplierId: values.supplierId,
      letterOfCreditId: values.letterOfCreditId,
      branchId: values.branchId,
      destinationWarehouseId: values.destinationWarehouseId,
      status: "draft",
      currency: values.currency,
      totalAmountCents: subtotalCents,
      totalAmountInBaseCents: subtotalCents, // mock; FX would be applied
      orderDate: Date.now(),
      expectedDeliveryDate: new Date(values.expectedDeliveryDate).getTime(),
      notes: values.notes || null,
      updatedAt: Date.now(),
      lineCount: values.lines.length,
    };
    setSeedRows((prev) => [newRow, ...prev]);
    setCreateOpen(false);
    toast.success(`PO ${newRow.poNumber} created (mock)`);
  }

  function handleEditSubmit(values: PoFormValues) {
    if (!editingPo) return;
    const subtotalCents = values.lines.reduce(
      (s, l) =>
        s + BigInt(Math.trunc(Math.max(l.orderedQuantity, 0))) * l.unitPriceCents,
      0n,
    );
    const now = Date.now();
    setSeedRows((prev) =>
      prev.map((r) =>
        r._id === editingPo._id
          ? {
              ...r,
              supplierId: values.supplierId,
              letterOfCreditId: values.letterOfCreditId,
              branchId: values.branchId,
              destinationWarehouseId: values.destinationWarehouseId,
              currency: values.currency,
              totalAmountCents: subtotalCents,
              totalAmountInBaseCents: subtotalCents,
              expectedDeliveryDate: new Date(values.expectedDeliveryDate).getTime(),
              notes: values.notes || null,
              updatedAt: now,
              lineCount: values.lines.length,
            }
          : r,
      ),
    );
    setDetailRow((prev) =>
      prev && prev._id === editingPo._id
        ? {
            ...prev,
            supplierId: values.supplierId,
            letterOfCreditId: values.letterOfCreditId,
            branchId: values.branchId,
            destinationWarehouseId: values.destinationWarehouseId,
            currency: values.currency,
            totalAmountCents: subtotalCents,
            totalAmountInBaseCents: subtotalCents,
            expectedDeliveryDate: new Date(values.expectedDeliveryDate).getTime(),
            notes: values.notes || null,
            updatedAt: now,
            lineCount: values.lines.length,
          }
        : prev,
    );
    setEditingPo(null);
    toast.success(`Updated ${editingPo.poNumber}. Audit trail recorded.`);
  }

  function handleEditSupplier(values: SupplierFormValues) {
    if (!editingSupplier) return;
    setLocalSuppliers((prev) =>
      prev.map((s) =>
        s._id === editingSupplier._id
          ? {
              ...s,
              displayName: values.displayName,
              legalName: values.legalName,
              countryCode: values.countryCode,
              defaultCurrency: values.defaultCurrency,
              paymentTermsDays: values.paymentTermsDays,
              contactPersonName: values.contactPersonName,
              contactEmail: values.contactEmail,
              contactPhone: values.contactPhone,
              openingBalanceBaseCents: values.openingBalanceBaseCents,
              openingBalanceAsOf: values.openingBalanceAsOf,
            }
          : s,
      ),
    );
    setEditingSupplier(null);
    toast.success(`Updated ${editingSupplier.displayName} (mock)`);
  }

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<PoRow>[] = [
      { key: "poNumber", label: "PO #" },
      {
        key: "supplierId",
        label: "Supplier",
        value: (r) => findSupplier(r.supplierId)?.displayName ?? r.supplierId,
      },
      {
        key: "letterOfCreditId",
        label: "LC",
        value: (r) =>
          r.letterOfCreditId
            ? findLc(r.letterOfCreditId)?.lcNumber ?? r.letterOfCreditId
            : "",
      },
      {
        key: "branchId",
        label: "Branch",
        value: (r) =>
          branches.find((b) => b._id === r.branchId)?.displayName ?? r.branchId,
      },
      {
        key: "destinationWarehouseId",
        label: "Warehouse",
        value: (r) =>
          r.destinationWarehouseId
            ? findWarehouse(r.destinationWarehouseId)?.displayName ?? ""
            : "",
      },
      { key: "status", label: "Status" },
      { key: "totalAmountCents", label: "Total" },
      { key: "currency", label: "Currency" },
      { key: "totalAmountInBaseCents", label: "Total BDT" },
      { key: "orderDate", label: "Order date", format: (v) => formatDateForCsv(Number(v)) },
      {
        key: "expectedDeliveryDate",
        label: "Expected delivery",
        format: (v) => formatDateForCsv(v as number | null),
      },
      { key: "updatedAt", label: "Updated", format: (v) => formatDateForCsv(Number(v)) },
    ];
    downloadCsv(
      exportFilename("purchase-orders", extension),
      toCsv(filtered, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Purchase Orders"
        description="Issue and reconcile POs with overseas suppliers."
        actions={
          <>
            {canSeeAmounts ? (
              <ExportButton
                onCsv={() => handleExportCsv("csv")}
                onExcel={() => handleExportCsv("xls")}
              />
            ) : null}
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New PO
            </Button>
          </>
        }
      />

      <section
        className={
          canSeeAmounts
            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            : "grid grid-cols-1 sm:grid-cols-3 gap-4"
        }
        aria-label="PO summary"
      >
        <KpiCard caption="Open POs" value={openPOs.length} />
        {canSeeAmounts ? (
          <KpiCard
            caption="Total open value"
            value={formatMoneySmart(totalOpenBdt, "BDT", { locale: "en-BD" })}
            hint="In BDT (snapshot rates)"
          />
        ) : null}
        <KpiCard caption="Awaiting receipt" value={awaitingReceipt} />
        <KpiCard caption="This month" value={thisMonth} />
      </section>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          {TAB_OPTIONS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailRow(row.original)}
        emptyTitle="No POs match"
        emptyDescription={
          rows.length === 0
            ? "Draft a PO to a vendor to start tracking imports here."
            : "Try a different tab."
        }
        emptyIcon={<ClipboardList className="h-5 w-5" aria-hidden />}
        emptyAction={
          rows.length === 0 ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New PO
            </Button>
          ) : undefined
        }
      />

      {/* Create */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>New Purchase Order</SheetTitle>
            <SheetDescription>
              FX rate is snapshotted at posting time. Multi-currency totals
              are stored alongside their BDT-equivalent.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <CreatePoContainer
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
              suppliers={localSuppliers}
              lettersOfCredit={lettersOfCredit}
              branches={branches}
              warehouses={warehouses}
              products={products}
              units={units}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRow(null);
            setEditingPo(null);
          }
        }}
      >
        <SheetContent side="right" size="lg">
          {detailRow && editingPo ? (
            <>
              <SheetHeader>
                <SheetTitle>Edit {editingPo.poNumber}</SheetTitle>
                <SheetDescription>
                  Edits update line totals and re-snapshot the FX equivalent.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <EditPoContainer
                  row={editingPo}
                  onSubmit={handleEditSubmit}
                  onCancel={() => setEditingPo(null)}
                  suppliers={localSuppliers}
                  lettersOfCredit={lettersOfCredit}
                  branches={branches}
                  warehouses={warehouses}
                  products={products}
                  units={units}
                />
              </SheetBody>
            </>
          ) : detailRow ? (
            <PoDetail
              row={detailRow}
              onEdit={() => setEditingPo(detailRow)}
              onEditSupplier={(suppId) => {
                const sup = localSuppliers.find((s) => s._id === suppId);
                if (sup) setEditingSupplier(sup);
              }}
              shipments={shipments}
              products={products}
              units={units}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Edit supplier Sheet */}
      <Sheet
        open={editingSupplier !== null}
        onOpenChange={(open) => {
          if (!open) setEditingSupplier(null);
        }}
      >
        <SheetContent side="right" size="md">
          {editingSupplier ? (
            <>
              <SheetHeader>
                <SheetTitle>Edit {editingSupplier.code}</SheetTitle>
                <SheetDescription>
                  Update supplier details and opening AP balance.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <SupplierForm
                  isEdit
                  initialValues={{
                    code: editingSupplier.code,
                    displayName: editingSupplier.displayName,
                    legalName: editingSupplier.legalName ?? "",
                    countryCode: editingSupplier.countryCode,
                    defaultCurrency: editingSupplier.defaultCurrency,
                    paymentTermsDays: editingSupplier.paymentTermsDays,
                    contactPersonName: editingSupplier.contactPersonName ?? "",
                    contactEmail: editingSupplier.contactEmail ?? "",
                    contactPhone: editingSupplier.contactPhone ?? "",
                    openingBalanceBaseCents:
                      editingSupplier.openingBalanceBaseCents ?? 0n,
                    openingBalanceAsOf:
                      editingSupplier.openingBalanceAsOf ?? null,
                  }}
                  onSubmit={handleEditSupplier}
                  onCancel={() => setEditingSupplier(null)}
                />
              </SheetBody>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Edit container — re-uses the same product/supplier options as create.
 * ───────────────────────────────────────────────────────────── */

function EditPoContainer({
  row,
  onSubmit,
  onCancel,
  suppliers,
  lettersOfCredit,
  branches,
  warehouses,
  products,
  units,
}: {
  row: PoRow;
  onSubmit: (v: PoFormValues) => void;
  onCancel: () => void;
  suppliers: MockSupplier[];
  lettersOfCredit: MockLetterOfCredit[];
  branches: MockBranch[];
  warehouses: MockWarehouseRecord[];
  products: MockProductRecord[];
  units: MockUnitRecord[];
}) {
  const [supplierId, setSupplierId] = useState<string>(row.supplierId);
  const lcsForSupplier = useMemo(() => {
    return lettersOfCredit
      .filter(
        (l) =>
          l.supplierId === supplierId &&
          (l.status === "issued" ||
            l.status === "partially_used" ||
            l._id === row.letterOfCreditId),
      )
      .map((l) => ({
        id: l._id,
        lcNumber: l.lcNumber,
        status: l.status,
      }));
  }, [supplierId, row.letterOfCreditId, lettersOfCredit]);

  // We don't carry full lines through to the row in mock. Synthesize one line
  // from the row's totals so the form has something pre-filled.
  const initialLines = useMemo(() => {
    const product = products[0]!;
    return [
      {
        productId: product._id,
        description: "",
        orderedQuantity: 100,
        unitId: product.primaryUnitId,
        unitPriceCents: row.totalAmountCents / 100n,
      },
    ];
  }, [row.totalAmountCents, products]);

  const supplierOptions = useMemo(
    () =>
      suppliers
        .filter((s) => s.deletedAt === null)
        .map((s) => ({
          id: s._id,
          displayName: s.displayName,
          defaultCurrency: s.defaultCurrency,
        })),
    [suppliers],
  );
  const branchOptions = useMemo(
    () => branches.map((b) => ({ id: b._id, displayName: b.displayName })),
    [branches],
  );
  const warehouseOptions = useMemo(
    () => warehouses.map((w) => ({ id: w._id, displayName: w.displayName })),
    [warehouses],
  );
  const productOptions = useMemo(
    () =>
      products
        .filter((p) => p.isActive)
        .map((p) => ({
          id: p._id,
          sku: p.sku,
          displayName: p.displayName,
          primaryUnitId: p.primaryUnitId,
        })),
    [products],
  );
  const unitOptions = useMemo(
    () =>
      units.map((u) => ({
        id: u._id,
        code: u.code,
        displayName: u.displayName,
      })),
    [units],
  );

  return (
    <PoForm
      submitLabel="Save changes"
      suppliers={supplierOptions}
      branches={branchOptions}
      warehouses={warehouseOptions}
      lcs={lcsForSupplier}
      products={productOptions}
      units={unitOptions}
      onSupplierChange={setSupplierId}
      initialValues={{
        supplierId: row.supplierId,
        letterOfCreditId: row.letterOfCreditId,
        branchId: row.branchId,
        destinationWarehouseId: row.destinationWarehouseId,
        currency: row.currency,
        expectedDeliveryDate: row.expectedDeliveryDate
          ? new Date(row.expectedDeliveryDate).toISOString().slice(0, 10)
          : "",
        incoterms: "FOB",
        portOfLoading: "",
        portOfDischarge: "Chittagong",
        notes: row.notes ?? "",
        lines: initialLines,
      }}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  );
}

/* ─────────────────────────────────────────────────────────────
 * Create container — owns supplier-filtered LC list state.
 * ───────────────────────────────────────────────────────────── */

function CreatePoContainer({
  onSubmit,
  onCancel,
  suppliers,
  lettersOfCredit,
  branches,
  warehouses,
  products,
  units,
}: {
  onSubmit: (v: PoFormValues) => void;
  onCancel: () => void;
  suppliers: MockSupplier[];
  lettersOfCredit: MockLetterOfCredit[];
  branches: MockBranch[];
  warehouses: MockWarehouseRecord[];
  products: MockProductRecord[];
  units: MockUnitRecord[];
}) {
  const [supplierId, setSupplierId] = useState<string>("");
  const lcsForSupplier = useMemo(() => {
    if (!supplierId) return [];
    return lettersOfCredit
      .filter(
        (l) =>
          l.supplierId === supplierId &&
          (l.status === "issued" || l.status === "partially_used"),
      )
      .map((l) => ({
        id: l._id,
        lcNumber: l.lcNumber,
        status: l.status,
      }));
  }, [supplierId, lettersOfCredit]);

  const supplierOptions = useMemo(
    () =>
      suppliers
        .filter((s) => s.deletedAt === null)
        .map((s) => ({
          id: s._id,
          displayName: s.displayName,
          defaultCurrency: s.defaultCurrency,
        })),
    [suppliers],
  );
  const branchOptions = useMemo(
    () => branches.map((b) => ({ id: b._id, displayName: b.displayName })),
    [branches],
  );
  const warehouseOptions = useMemo(
    () => warehouses.map((w) => ({ id: w._id, displayName: w.displayName })),
    [warehouses],
  );
  const productOptions = useMemo(
    () =>
      products
        .filter((p) => p.isActive)
        .map((p) => ({
          id: p._id,
          sku: p.sku,
          displayName: p.displayName,
          primaryUnitId: p.primaryUnitId,
        })),
    [products],
  );
  const unitOptions = useMemo(
    () =>
      units.map((u) => ({
        id: u._id,
        code: u.code,
        displayName: u.displayName,
      })),
    [units],
  );

  return (
    <PoForm
      suppliers={supplierOptions}
      branches={branchOptions}
      warehouses={warehouseOptions}
      lcs={lcsForSupplier}
      products={productOptions}
      units={unitOptions}
      onSupplierChange={setSupplierId}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  );
}

/* ─────────────────────────────────────────────────────────────
 * Detail Sheet
 * ───────────────────────────────────────────────────────────── */

function PoDetail({
  row,
  onEdit,
  onEditSupplier,
  shipments,
  products,
  units,
}: {
  row: PoRow;
  onEdit: () => void;
  onEditSupplier?: (supplierId: string) => void;
  shipments: MockShipmentRecord[];
  products: MockProductRecord[];
  units: MockUnitRecord[];
}) {
  const supplier = findSupplier(row.supplierId);
  const lc = findLc(row.letterOfCreditId);
  const wh = findWarehouse(row.destinationWarehouseId);
  const linkedShipments = useMemo(
    () => shipments.filter((s) => s.purchaseOrderId === row._id),
    [shipments, row._id],
  );
  const isLocked =
    row.status === "received" ||
    row.status === "closed" ||
    row.status === "cancelled";
  // We don't carry full lines in mock; we synthesize "the first available"
  // line so the detail Sheet is non-empty.
  const fakeLines = useMemo(() => {
    const product = products[0];
    if (!product) return [];
    const unit = units.find((u) => u._id === product.primaryUnitId);
    return [
      {
        productId: product._id,
        productLabel: product.displayName,
        productSku: product.sku,
        quantity: 100,
        unitId: product.primaryUnitId,
        unitLabel: unit?.code ?? "pcs",
        unitPriceCents: row.totalAmountCents / 100n,
        lineTotalCents: row.totalAmountCents,
      },
    ];
  }, [row, products, units]);

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span className="font-mono">{row.poNumber}</span>
          <PoStatusBadge status={row.status} />
          <RecordAuditBadge tableName="purchaseOrders" recordId={row._id} />
          <AttachmentBadge
            parentTable="purchaseOrders"
            parentRecordId={row._id}
          />
        </SheetTitle>
        <SheetDescription>
          {supplier?.displayName ?? "—"} · {row.currency} ·{" "}
          {wh?.displayName ?? "—"}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
          <section className="grid grid-cols-2 gap-3">
            <DetailField
              label="Total"
              value={formatMoney(row.totalAmountCents, row.currency, {
                display: "code",
                locale: "en-BD",
              })}
            />
            <DetailField
              label="Total (BDT)"
              value={formatMoney(row.totalAmountInBaseCents, "BDT", {
                locale: "en-BD",
              })}
            />
            <DetailField label="Order date" value={formatDate(row.orderDate)} />
            <DetailField
              label="Expected"
              value={formatDate(row.expectedDeliveryDate)}
            />
            {lc ? (
              <div className="col-span-2 flex items-center gap-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--info-soft) px-3 py-2">
                <span className="text-[11px] uppercase tracking-wide text-(--info)">
                  LC
                </span>
                <span className="font-mono text-[12px] text-(--text-primary)">
                  {lc.lcNumber}
                </span>
                <LcStatusBadge status={lc.status} size="sm" />
              </div>
            ) : null}
          </section>

          {/* Supplier summary + quick edit link */}
          {supplier ? (
            <section className="rounded-(--radius-md) border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2.5 flex items-center justify-between gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="text-[12px] font-medium text-(--text-primary)">
                  {supplier.displayName}
                </span>
                <span className="text-[11px] text-(--text-tertiary)">
                  {supplier.code} · {supplier.countryCode} ·{" "}
                  {supplier.defaultCurrency}
                </span>
              </div>
              {onEditSupplier ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEditSupplier(row.supplierId)}
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit supplier
                </Button>
              ) : null}
            </section>
          ) : null}

          {/* Lines */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Lines
            </div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-(--text-tertiary) border-b border-(--border-subtle)">
                  <th className="py-2">Product</th>
                  <th className="text-right">Qty</th>
                  <th>Unit</th>
                  <th className="text-right">Unit price</th>
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {fakeLines.map((l, i) => (
                  <tr
                    key={i}
                    className="border-b border-(--border-subtle)"
                  >
                    <td className="py-2">
                      {l.productLabel}{" "}
                      <span className="font-mono text-[11px] text-(--text-tertiary)">
                        {l.productSku}
                      </span>
                    </td>
                    <td className="text-right tabular-nums">{l.quantity}</td>
                    <td className="text-(--text-secondary)">{l.unitLabel}</td>
                    <td className="text-right font-mono tabular-nums">
                      {formatMoney(l.unitPriceCents, row.currency, {
                        display: "code",
                        locale: "en-BD",
                      })}
                    </td>
                    <td className="text-right font-mono tabular-nums">
                      {formatMoney(l.lineTotalCents, row.currency, {
                        display: "code",
                        locale: "en-BD",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Shipments */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Shipments ({linkedShipments.length})
            </div>
            {linkedShipments.length === 0 ? (
              <div className="text-[12px] text-(--text-tertiary)">
                No shipments yet for this PO.
              </div>
            ) : (
              <ul className="flex flex-col gap-1.5 text-[13px]">
                {linkedShipments.map((s) => (
                  <li
                    key={s._id}
                    className={cn(
                      "flex items-center justify-between gap-3",
                      "rounded-(--radius-md) border border-(--border-subtle)",
                      "bg-(--bg-canvas) px-3 py-2",
                    )}
                  >
                    <span className="font-mono text-[12px] text-(--text-primary)">
                      {s.shipmentNumber}
                    </span>
                    <span className="text-(--text-secondary)">
                      {s.carrierName ?? "—"}
                    </span>
                    <ShipmentStatusBadge status={s.status} size="sm" pulse />
                  </li>
                ))}
              </ul>
            )}
          </section>

          {row.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                Notes
              </div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                {row.notes}
              </p>
            </section>
          ) : null}
            </div>
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail tableName="purchaseOrders" recordId={row._id} />
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            <AttachmentList
              parentTable="purchaseOrders"
              parentRecordId={row._id}
              purposeOptions={PO_PURPOSE_OPTIONS}
              allowCustomPurpose
              mimeAllowlist={["image/*", "application/pdf"]}
              title="Drop PO documents here"
              description="Signed PO, supplier acknowledgements, quotes. Max 10 MB per file."
            />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        {isLocked ? (
          <Tooltip content="Cannot edit closed records — create a credit note instead">
            <span>
              <Button variant="ghost" size="icon" disabled aria-label="Edit">
                <Pencil className="size-4" />
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit">
            <Pencil className="size-4" />
          </Button>
        )}
        {row.status === "draft" ? (
          <Button onClick={() => toast.info("Submit — wired in next phase")}>
            Submit
          </Button>
        ) : null}
        {row.status === "submitted" ? (
          <Button onClick={() => toast.info("Acknowledge — wired in next phase")}>
            Acknowledge
          </Button>
        ) : null}
        {row.status !== "received" &&
        row.status !== "closed" &&
        row.status !== "cancelled" ? (
          <Button
            variant="outline"
            onClick={() =>
              toast.info(
                row.letterOfCreditId
                  ? "Unlink LC — wired in next phase"
                  : "Link LC — wired in next phase",
              )
            }
          >
            {row.letterOfCreditId ? "Unlink LC" : "Link LC"}
          </Button>
        ) : null}
        {row.status !== "received" &&
        row.status !== "closed" &&
        row.status !== "cancelled" ? (
          <Button
            variant="destructive"
            onClick={() => toast.info("Cancel — wired in next phase")}
          >
            Cancel
          </Button>
        ) : null}
      </SheetFooter>
    </>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums">
        {value}
      </div>
    </div>
  );
}
